import React, { useEffect, useMemo, useState } from 'react';
import { TextField, Select, MenuItem, FormControl, InputLabel, Grid, Typography, Box } from '@mui/material';
import useStyles from '../../../../styles/global-mui.js';
import { useDispatch } from 'react-redux';
import { discipline_all, getActualSeason, season_all, subscription_all } from '../../../../bdd/bddSTUB.js';
import { checkExistingTeam, fetchCompetitions } from '../../../../api/index.js';
import { getUserProvider } from '../../../../actions/auth.js';
import SelectGroupProvider from '../../../../dsasComponents/SelectGroupProvider.js';
import { useLocation, useNavigate } from 'react-router-dom';
import { createTeam, deleteTeam, updateTeam } from '../../../../actions/teams.js';
import TeamCheckModal from './TeamCheckModal.js';
import { ColorPicker } from '../../../components/ColorPicker.js';
import { confirmActionNotification } from '../../../../actions/notifications.js';
import { useNotifications } from '../../../../v2/notifications/NotificationManager.js';
import { Can } from '../../../../permissions/Can.js';
import { PERMISSIONS } from '../../../../permissions/permissionKeys.js';
import DsasButton from '../../../components/DsasButton/DsasButton.js';
import ImageHandlerTool from '../../../components/ImageHandlerTool.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleXmark, faCircleCheck, faDroplet, faPlus, faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { faCircle } from '@fortawesome/free-regular-svg-icons';
import { usePermissions } from '../../../../permissions/usePermissions.js';

const defaultTeamData = {
    name : '',
    fullName : '', 
    discipline : '', 
    logo : '', 
    color : '', 
    awayColor : '', 
    competitions : [], 
    isCountry : false, 
    competitionsPenality : [],

    stadiumName : '',
    stadiumCapacity : '',
    photoStadium : '',
    country : '', 
    teamCreation : '',
    website : '',
    president : '',
    coaches : '',

    subscription : 1,

    provider : getUserProvider()
}

const getSeasonStart = (season) => {
  if (!season) return -Infinity;
  const first = String(season).split('/')[0];
  const n = parseInt(first, 10);
  return Number.isFinite(n) ? n : -Infinity;
};

const FormTeam = ({ team = defaultTeamData, onValidate = () => {} }) => {

    const navigate = useNavigate()
    const location = useLocation()
    const {addNotification} = useNotifications()

    const { getPermission } = usePermissions();
    const providerPermission = getPermission(PERMISSIONS.FILTER_PROVIDER);

    const teamFromState = location.state?.copyTeam || team;
    const [teamData, setTeamData] = useState(teamFromState);

    const [filters, setFilters] = useState({
        discipline: teamData.discipline || '',
        provider: teamData.provider || '',
        season: getActualSeason(),
    });
    const [availableCompetitions, setAvailableCompetitions] = useState([]);
    const dispatch = useDispatch();

    const [existingTeams, setExistingTeams] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCheckComplete, setIsCheckComplete] = useState(team._id || location.state?.copyTeam); // Gérer l'étape de vérification

    const [showFilters, setShowFilters] = useState(false); // Gérer l'affichage des filtres pour ajouter une équipe

    const [isProcessing, setIsProcessing] = useState(false)

    const loadCompetitions = async () => {
        const response = await fetchCompetitions(filters);
        setAvailableCompetitions(response.data || []);
    };

    // Charger les compet en fonction de l'equipe
    useEffect(() => {
        if (filters.season) loadCompetitions();
    }, [filters]);
    
    // Charger les compétitions
    useEffect(() => {
    setFilters(s => ({...s, provider : teamData.provider, discipline : teamData.discipline}))
    }, [teamData.provider, teamData.discipline]);

    // Utiliser un useEffect pour surveiller les changements de playerData.teams
    useEffect(() => {
        loadCompetitions(); // Recharger les équipes chaque fois que playerData.teams change
    }, [teamData.competitions, filters]); 

    // Gérer la soumission du formulaire
    const handleSubmit = async (e) => {

        setIsProcessing(true)

        const sanitizedTeamData = { ...teamData };

        // Nettoyer les champs facultatifs
        /*['stadiumCapacity'].forEach((key) => {
        if (!sanitizedTeamData[key]) delete sanitizedTeamData[key];
        });*/

        // Refactoriser teamsSeason
        sanitizedTeamData.competitionsPenality = (teamData.competitionsPenality || []).map(t => ({
        competition: t.competition._id,
        penality: t.penality
        }));
        // Créer ou mettre à jour l equipe
        if (team._id) {
            dispatch(updateTeam(team._id, sanitizedTeamData));
        } else {
            dispatch(createTeam(sanitizedTeamData));
        }

        const previousURL = location.state?.from || '/sports/teams'; // Redirige par défaut à la racine si aucune URL précédente
        setTimeout(() => {
            setIsProcessing(false); 
            onValidate();
            clearForm(); 
            navigate(previousURL)}, 
            1000)
    };

    // Gérer les changements dans les champs du formulaire
    const handleChange = (e) => {
        const { name, value } = e.target;
        setTeamData((prev) => ({ ...prev, [name]: value }));

        if (name === 'discipline') {
        setFilters((prev) => ({ ...prev, discipline: value }));
        }
    };

    const handleChangeIsCountry = () => {
        setTeamData(s => ({...s, isCountry: !s.isCountry}));
    };

    const handleAddCompetition = () => {
        setShowFilters(true); // Afficher les filtres pour ajouter une équipe
    };

    // Gestion de la sélection des competitions
    const handleSelectCompetition = (competitionId) => {

        const competition = availableCompetitions.find((c) => c._id === competitionId);

        if(competition) {
            setTeamData((prev) => ({
                ...prev,
                competitions: [...prev.competitions, competition._id],
                competitionsPenality: [
                  ...prev.competitionsPenality,
                  { competition, penality : 0 },
                ],
              }));
        }

        setShowFilters(false); // Masquer les filtres après la sélection
    };

    // Gestion de la suppression d'une compétition
    const handleDeleteCompetition = (competitionId) => {
        dispatch(confirmActionNotification("Êtes-vous sûr de vouloir supprimer la compétition ?", () => {
            const newCompetitionsPenality = (teamData.competitionsPenality || []).filter(
            (cp) => cp?.competition?._id !== competitionId
            );

            const newCompetitions = newCompetitionsPenality.map((cp) => cp.competition._id);

            setTeamData((prev) => ({
            ...prev,
            competitionsPenality: newCompetitionsPenality,
            competitions: newCompetitions,
            }));
        }))
        };

    const handleSelectPenality = (competitionId, penality) => {
        const newCompetitionsPenality = (teamData.competitionsPenality || []).map((cp) =>
            cp?.competition?._id === competitionId ? { ...cp, penality } : cp
        );

        setTeamData((prev) => ({ ...prev, competitionsPenality: newCompetitionsPenality }));
    };

    const isValidForm = () => {
        return isValidCheckExistingTeam() && teamData.provider && teamData.competitions.length
    }

    // Réinitialiser le formulaire
    const clearForm = () => {
        setTeamData(defaultTeamData);
        setIsCheckComplete(team._id);
        setIsModalOpen(false);
    };

    const isValidCheckExistingTeam = () => {
        return teamData.name && teamData.discipline
    }

    const handleCheckExistingTeam = async () => {
        const { name, discipline } = teamData;
        const results = await checkExistingTeam({ name, discipline });
    
        const similarTeams = results.data
        
        if(!similarTeams.length){
          setIsCheckComplete(true);
          setIsModalOpen(false);
        }else{
          setExistingTeams(similarTeams);
          setIsModalOpen(true); // Ouvrir la modal une fois que les résultats sont prêts
        }
    };

    const handleSelectOtherTeam = (team) => {
        // Remplir les champs avec les informations de l'équipe sélectionnée
        setTeamData((prev) => ({
          ...prev,
          name: team.name,
          fullName: team.fullName,
          discipline: team.discipline,
          color : team.color,
          awayColor : team.awayColor,
          textColor : team.textColor,

          logoName: team.logoName,
          logoUrl: team.logoUrl,

          stadiumName: team.stadiumName,
          stadiumCapacity: team.stadiumCapacity,
          photoStadiumName: team.photoStadiumName,
          photoStadiumUrl: team.photoStadiumUrl,

          country: team.country,
          teamCreation: team.teamCreation,
          website: team.website,
          president: team.president,
          coaches: team.coaches,
          subscription: team.subscription,

          thirdPartyId: team.thirdPartyId,
          thirdPartyEntityId: team.thirdPartyEntityId,
          // Ne pas inclure _id, teams, teamsSeason, provider
        }));
    
        setIsCheckComplete(true); // L'utilisateur a passé la vérification
        setIsModalOpen(false);
    };

    const handleConfirmNone = () => {
        setIsCheckComplete(true); // Marquer que l'utilisateur a bien vérifié
        setIsModalOpen(false);
    };

    const handleConfirmSubmit = (e) => {
    
        e.preventDefault()
    
        dispatch(confirmActionNotification("Êtes-vous sûr de vouloir "+ (team._id ? "modifier" : "créer")+" l'équipe ?", handleSubmit))

    }

    const handleDelete = (e) => {
        e.preventDefault()

        dispatch(confirmActionNotification("Êtes-vous sûr de vouloir supprimer l'équipe ?", () => {
            dispatch(deleteTeam(team._id, (id) => {addNotification({
                title : "Suppression", 
                type : "success", 
                description : "L'équipe "+ team.name+" a été supprimée."
            })
            const previousURL = location.state?.from || '/sports/teams';
            navigate(previousURL)
            }))
        })) 
    }

    const sortedAvailableCompetitions = useMemo(() => {
        return [...availableCompetitions].sort((a, b) => {
            const diff = getSeasonStart(b?.season) - getSeasonStart(a?.season);
            if (diff !== 0) return diff;
            return (a?.name || '').localeCompare(b?.name || '');
        });
        }, [availableCompetitions]);

        const sortedCompetitionsPenality = useMemo(() => {
        return [...(teamData.competitionsPenality || [])].sort((a, b) => {
            const diff = getSeasonStart(b?.competition?.season) - getSeasonStart(a?.competition?.season);
            if (diff !== 0) return diff;
            return (a?.competition?.name || '').localeCompare(b?.competition?.name || '');
        });
        }, [teamData.competitionsPenality]);

    const classes = useStyles();

    return (
        <Box sx={{ paddingLeft: 3, paddingRight: 3, textAlign : 'center', animation: isProcessing ? 'pulse 1.5s infinite' : 'none',
            '@keyframes pulse': {
              '0%': { opacity: 1 },
              '50%': { opacity: 0.5 },
              '100%': { opacity: 1 }
            } }}>
            <form onSubmit={handleConfirmSubmit} autoComplete="off" disabled={isProcessing}>
                {/*PAGE TITLE*/}
                <Box className='centered-medium-wrapper'>
                    <h1 className='page-title'>{team._id ? 'Editer' : 'Créer'} une équipe</h1>
                </Box>
                <Box className='large-separator'></Box>

                <Box className='small-padding'></Box>

                {/* <Typography variant="h6">{team._id ? 'Editer' : 'Créer'} une équipe</Typography> */}

                 <Box className='padding-wrapper'>
                          <Grid container spacing={1.5} sx={{alignItems : 'flex-end'}}>
                              {providerPermission?.allowed && ( 
                                <Grid item xs={12} sm={12} md>
                                  {/* Groupe (Provider) */}
                                  <SelectGroupProvider value={teamData.provider} onChange={handleChange} className={classes.customInput} />             
                                </Grid>
                                )}
                              <Grid item xs={12} sm={12} md>
                                {/* Nom */}
                                <TextField className={classes.customInput} 
                               name="name" label="Nom" error={!teamData.name} fullWidth value={teamData.name} onChange={handleChange} />             
                              </Grid>
                              <Grid item xs={12} sm={12} md>
                                {/* Discipline */}
                                <FormControl className={classes.customInput} fullWidth error={!teamData.discipline}>
                                    <InputLabel>Discipline</InputLabel>
                                    <Select name="discipline" value={teamData.discipline} onChange={handleChange} label={"Discipline"}>
                                        {discipline_all.map((discipline) => (
                                        <MenuItem key={discipline._id} value={discipline._id}>
                                            {discipline.name}
                                        </MenuItem>
                                        ))}
                                    </Select>
                                    </FormControl>        
                              </Grid>
                          </Grid>
                        </Box>

                 {!isCheckComplete && 
                        <Box className='centered-medium-wrapper'>
                          <DsasButton label="Vérifier l'existence de l'équipe" onClick={handleCheckExistingTeam} disabled={!isValidCheckExistingTeam()} variant = 'alternative'/>
                        </Box>
                        }


                {/* Modal de vérification des doublons */}
                <TeamCheckModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                results={existingTeams}
                onSelectOtherTeam={handleSelectOtherTeam}
                onConfirmNone={handleConfirmNone}
                actualProvider={teamData.provider}
                />

                {/* Formulaire complet, mais désactivé tant que la vérification n'est pas terminée */}
                {isCheckComplete && 
                       <>

                 <Box>

                    <Box className='centered-medium-wrapper'>
                    {/* Nom complet */}
                        <TextField className={classes.customInput}
                        name="fullName" label="Nom complet" fullWidth value={teamData.fullName} onChange={handleChange} />
                    </Box>
                    <Box className='small-padding'></Box>
                    <Box className='large-separator'></Box>

                    <Box 
                        className="left-medium-wrapper"
                        onClick={handleChangeIsCountry}
                        sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                        >
                        <FontAwesomeIcon
                            icon={teamData.isCountry ? faCircleCheck : faCircle }
                            style={{ color: 'var(--color-blue-secondary)', fontSize: 'larger' }}
                        />
                        <Typography className={classes.customTypo} sx={{ paddingLeft: '1rem' }}>Équipe nationale</Typography>
                        </Box>
                  


                    <Box className='large-separator'></Box>
                    
                    <Box className='centered-medium-wrapper'>
                        {/* Image */}
                        <ImageHandlerTool type='default' 
                            initialImage={team.logoUrl || teamData.logoUrl} 
                            onImageSave={(image) => setTeamData((prev) => ({ ...prev, logo: image })) } 
                            displayImageLabel={false} 
                            uploadButtonLabel='Editer le logo'/>
                    </Box>
                    
                    <Box className='large-separator'></Box>

                    <Box className='centered-medium-wrapper'>
                         <FontAwesomeIcon
                                  icon={faDroplet} style={{ color: 'var(--color-blue-secondary)', fontSize:'larger' }} />
                                <Grid container spacing={1.5}>
                                    <Grid item xs={12} sm={12} md={6} sx={{justifyItems: 'center'}}>
                                        <Box className='centered-wrapper'>
                                            <ColorPicker label="Couleur domicile" 
                                            color={teamData.color} 
                                            setColor={(color) => setTeamData(t => ({...t, color}))}/>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6} sx={{justifyItems: 'center'}}>
                                        <Box className='centered-wrapper'>
                                            <ColorPicker label="Couleur extérieure" 
                                            color={teamData.awayColor} 
                                            setColor={(awayColor) => setTeamData(t => ({...t, awayColor}))}/>
                                        </Box>
                                    </Grid>
                                </Grid>
                    </Box>
                    <Box className='large-separator'></Box>
                

                <Box className='padding-wrapper'>
                    <Typography variant='h1' className={classes.customSubtitle}>
                            Historique des compétitions
                    </Typography>
                    <Box className='centered-wrapper'>
                        <DsasButton label="Ajouter une compétition" icon={faPlus} onClick={handleAddCompetition} variant = 'secondary' disabled={showFilters} />
                    </Box>
                    {showFilters && (
                        <Box sx={{ mb: 3 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <FormControl className={classes.customInput} fullWidth>
                                        <InputLabel>Saison</InputLabel>
                                        <Select name="season" label="Saison" value={filters.season} onChange={(e) => setFilters((prev) => ({ ...prev, season: e.target.value }))}>
                                        {season_all.map((season) => (
                                            <MenuItem key={season._id} value={season._id}>
                                            {season.name}
                                            </MenuItem>
                                        ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Sélection des équipes */}
                                <Grid item xs={12} sm={6}>
                                    <FormControl className={classes.customInput} fullWidth>
                                    <InputLabel>Competition</InputLabel>
                                    <Select
                                        value=""
                                        onChange={(e) => handleSelectCompetition(e.target.value)}
                                    >
                                        {sortedAvailableCompetitions.map((competition) => (
                                        <MenuItem key={competition._id} value={competition._id}>
                                            {competition.name}
                                        </MenuItem>
                                        ))}
                                    </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>

                        </Box>
                    )}

                    {sortedCompetitionsPenality.map((competitionInfo, idx) => (
                            <React.Fragment key={competitionInfo.competition._id}>
                            <Box className='medium-padding'>
                                <Grid container alignItems="center" spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            {competitionInfo.competition.name}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                        <TextField
                                                className={classes.customInput}
                                                    label='Saison'
                                                    value={competitionInfo.competition.season}
                                                    disabled
                                                />
                                    </Grid>

                                    <Grid item xs={12} sm={3}>
                                                <TextField
                                                className={classes.customInput}
                                                    label='Penalité'
                                                    value={competitionInfo.penality}
                                                    onChange={(e) => handleSelectPenality(competitionInfo.competition._id, e.target.value)}
                                                />
                                    </Grid>

                                    {/* Delete button */}
                                    <Grid item xs={12} sm={2} sx={{ textAlign: 'right' }}>
                                    <Can permission={PERMISSIONS.SPORT_TEAM_CREATE_BUTTON_REMOVEPENALITY}>
                                    <Box className='centered-wrapper'>
                                        <DsasButton label="Supprimer" icon={faTrashCan} onClick={() => handleDeleteCompetition(competitionInfo.competition._id)} variant = 'secondary' disabled={showFilters} />
                                    </Box>
                                    </Can>
                                    </Grid>
                                </Grid>
                            </Box>
                            {idx < sortedCompetitionsPenality.length - 1 && <Box className='large-separator'></Box>}
                            </React.Fragment>
                        ))
                    }
               </Box>

                <Box className='large-separator'></Box>

                <Box className='centered-medium-wrapper'>
                <Typography variant='h1' className={classes.customSubtitle}>
                            Informations
                    </Typography>
                </Box>

                <Box className='centered-medium-wrapper'>
                    <Grid container alignItems="center" spacing={2} sx={{
                                    justifyContent: "flex-start",
                                    alignItems: "center",
                                }}>
                        <Grid item xs={12} sm={6} md={8} direction="column"
                                sx={{
                                    justifyContent: "flex-start",
                                    alignItems: "center",
                                    marginBottom: "auto"
                                }}>
                                {/* StadiumName */}
                                <TextField
                                    className={classes.customInput}
                                    name="stadiumName"
                                    label="Stade"
                                    fullWidth
                                    value={teamData.stadiumName || ''}
                                    onChange={handleChange}
                                    
                                />
                                <Box className='medium-padding'></Box>

                                {/* StadiumName */}
                                <TextField
                                    className={classes.customInput}
                                    name="stadiumCapacity"
                                    label="Capacité"
                                    fullWidth
                                    value={teamData.stadiumCapacity || ''}
                                    onChange={handleChange}
                                    
                                />
                        

                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            {/* Image du stade */}
                            <ImageHandlerTool type='default' 
                            initialImage={teamData.photoStadiumUrl} 
                            onImageSave={(image) => setTeamData((prev) => ({ ...prev, photoStadium: image })) } 
                            displayImageLabel={false} uploadButtonLabel='Editer le stade' />
                        </Grid>
                    </Grid>
                </Box>

                <Box className='large-separator'></Box>
                <Box className='small-padding'></Box>

                <Box
                    className='centered-medium-wrapper'
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 4, // Espacement régulier entre les champs
                            alignItems: 'center',
                        }}
                    >

                    {/* Country */}
                    <TextField
                        className={classes.customInput}
                        name="country"
                        label="Pays"
                        fullWidth
                        value={teamData.country || ''}
                        onChange={handleChange}
                    />

                

                <TextField 
                className={classes.customInput}
                    name="teamCreation" 
                    variant="outlined"
                    label="Année de création" 
                    fullWidth
                    value={teamData.teamCreation || ''}
                    onChange={handleChange}
                />

                <TextField 
                className={classes.customInput}
                    name="website" 
                    variant="outlined"
                    label="Site web" 
                    fullWidth
                    value={teamData.website || ''}
                    onChange={handleChange}
                />

                <TextField 
                className={classes.customInput}
                    name="president" 
                    variant="outlined"
                    label="Président" 
                    fullWidth
                    value={teamData.president || ''}
                    onChange={handleChange}
                />

                <TextField 
                className={classes.customInput}
                    name="coaches" 
                    variant="outlined"
                    label="Coaches (coach1/coach2/coach3...)" 
                    fullWidth
                    multiline
                    rows={3}
                    value={teamData.coaches || ''}
                    onChange={handleChange}
                />
                </Box>

                <Box className='small-padding'></Box>

                <Box className='large-separator'></Box>

                 <Can permission={PERMISSIONS.AREA_THIRDPARTY}>
                    <Box className='medium-padding'>

                        <h2 className='page-subtitle'>Identification externe</h2> 

                        <Box className='small-padding'></Box>
        
                            <Grid container spacing={1.5}>
                                        <Grid item xs={6} sm={6} md>
                                            {/* Id externe */}
                                            <TextField 
                                            className={classes.customInput}
                                            name="thirdPartyEntityId" label="Id externe" fullWidth value={teamData.thirdPartyEntityId || ''} onChange={handleChange} />   
                                        </Grid>
                                        <Grid item xs={6} sm={6} md>
                                            {/* Origine id externe */}
                                            <TextField 
                                            className={classes.customInput}
                                            name="thirdPartyId" label="Origine Id externe" fullWidth value={teamData.thirdPartyId || ''} onChange={handleChange} />  
                                        </Grid>
                            </Grid>
                            <Box className='small-padding'></Box>
                    </Box>   
                    <Box className='large-separator'></Box>      
                </Can>

                {/*Subscription*/}
                <Can permission={PERMISSIONS.SPORT_TEAM_CREATE_BUTTON_DIRECTTYPE}>
                    <Box className='centered-column-wrapper medium-wrapper'>
                        <Box className='centered-wrapper'>
                                    <Typography variant='h1' className={classes.customSubtitle}>
                                                Options de direct
                                        </Typography>
                        </Box>
                        
                            <FormControl className={classes.customInput} fullWidth margin="normal" >
                                <InputLabel id={"subscription"}>Type de direct</InputLabel>
                                <Select
                                    labelId={'subscription'}
                                    id={"subscriptionselect"}
                                    name={'subscription'}
                                    value={teamData.subscription}
                                    label={'Subscription'}
                                    onChange={handleChange}
                                >
                                    {subscription_all && subscription_all.map(i => (<MenuItem key={i._id} value={i._id} selected >{i.name ? i.name : `${i.lastname} ${i.firstname}`}</MenuItem>))}
                                </Select>
                            </FormControl>
                        
                    </Box>
                </Can>

                <Box className='medium-padding'>
                    {teamData.competitionsPenality?.length === 0 && 
                        <Typography 
                            className={classes.customTypoColor} 
                            padding='10px' 
                            justifySelf='center' 
                            fontSize="small" 
                            fontStyle="italic"> Veuillez ajouter une compétition pour poursuivre
                        </Typography>}
                    {/* Boutons */}
                    <Box className='small-padding'></Box>
                    <Box className='centered-wrapper'>
                        <DsasButton type='submit' label="Valider" variant = 'alternative' disabled={!isValidForm()} />
                    </Box>
                    <Box className='small-padding'></Box>
                    {team._id && 
                    <Can permission={PERMISSIONS.SPORT_TEAM_CREATE_BUTTON_DELETE}>
                        <DsasButton icon={faTrashCan} label="Supprimer" variant = 'secondary' onClick={handleDelete} />
                    </Can> 
                        }
                </Box>
                <Box className='medium-padding'></Box>

                </Box></>}

            </form>
        </Box>
    );

};

export default FormTeam;